/**
 * Zod schemas for the grooming API routes SpecTree Toolbox calls (epic
 * PI-SPEC-SW-E279). Prefix: `/api/v1/grooming`.
 *
 * These are the one definition: the SpecTree API validates its requests with
 * them, and the Toolbox engine validates what it sends and what comes back.
 * A version skew between the two then fails at the boundary with a clear
 * Zod error instead of deep inside a run.
 *
 * Covered routes (the engine's calls, nothing more):
 * - `GET  /projects/:project/config`           project config read
 * - `GET  /projects/:project/candidates`       selection read
 * - `POST /batches`                            create a batch
 * - `POST /batches/:id/items`                  add items to a draft batch
 * - `POST /batches/:id/start`                  start a batch
 * - `PATCH /batches/:id/items/:batchItemId`    report one item's progress
 * - `POST /batches/:id/finish`                 finish a batch
 * - `GET  /batches/:id`                        a batch with its items
 * - `POST /batches/:id/heartbeat`              the running batch is still being prepared (contract 3)
 *
 * Contract 5 (ER-1712) adds the body and response schemas of the disregard
 * routes `POST .../grooming-briefs/:briefId/disregard` and
 * `POST .../grooming-briefs/:briefId/undo-disregard` (web pages call them).
 *
 * And, under `/api/v1/work-items` (contract 2, epic PI-SPEC-SW-E280):
 * - `PUT  /:tracker/:project/:itemId/author-questions/draft`  save drafted author questions
 * - `GET  /:tracker/:project/:itemId/author-questions/draft`  read the current draft
 *
 * Request schemas are strict (unknown keys are refused). Response schemas
 * strip unknown keys, so a SpecTree release may add response fields without
 * a contract bump. Every response is wrapped as `{ data: ... }`; use
 * {@link apiDataSchema} to parse the envelope.
 */
import { z } from "zod";
import { isoDateTime } from "../zod-helpers.js";
import { MAX_GROOMING_BATCH_ITEMS } from "./constants.js";
import { GROOMING_BATCH_ITEM_STATUSES, GROOMING_BATCH_MODES, GROOMING_BATCH_STATUSES, GROOMING_REPO_HOSTS, GROOMING_REVIEW_STATES, GROOMING_STAGES, GROOMING_STOP_REASONS, } from "./types.js";
/** Path prefix of every grooming route. */
export const GROOMING_API_PREFIX = "/api/v1/grooming";
/** Path prefix of the work-item routes (briefs and author questions live here). */
export const WORK_ITEMS_API_PREFIX = "/api/v1/work-items";
/** Paths of the routes this module covers, relative to the SpecTree origin. */
export const groomingApiPaths = {
    projectConfig: (project) => `${GROOMING_API_PREFIX}/projects/${encodeURIComponent(project)}/config`,
    candidates: (project) => `${GROOMING_API_PREFIX}/projects/${encodeURIComponent(project)}/candidates`,
    batches: () => `${GROOMING_API_PREFIX}/batches`,
    batch: (batchId) => `${GROOMING_API_PREFIX}/batches/${encodeURIComponent(batchId)}`,
    batchItems: (batchId) => `${GROOMING_API_PREFIX}/batches/${encodeURIComponent(batchId)}/items`,
    batchItem: (batchId, batchItemId) => `${GROOMING_API_PREFIX}/batches/${encodeURIComponent(batchId)}/items/${encodeURIComponent(batchItemId)}`,
    startBatch: (batchId) => `${GROOMING_API_PREFIX}/batches/${encodeURIComponent(batchId)}/start`,
    finishBatch: (batchId) => `${GROOMING_API_PREFIX}/batches/${encodeURIComponent(batchId)}/finish`,
    /**
     * Contract 3: the app tells SpecTree, every {@link GROOMING_HEARTBEAT_INTERVAL_MS},
     * that a running batch is still being prepared on this computer.
     */
    batchHeartbeat: (batchId) => `${GROOMING_API_PREFIX}/batches/${encodeURIComponent(batchId)}/heartbeat`,
    /** Contract 2: the stored draft of questions for an item's author (`PUT` saves, `GET` reads). */
    authorQuestionsDraft: (tracker, project, itemId) => `${WORK_ITEMS_API_PREFIX}/${encodeURIComponent(tracker)}/${encodeURIComponent(project)}/${encodeURIComponent(itemId)}/author-questions/draft`,
};
/**
 * Contract 3. How long a running batch's "Being prepared by <person>" marker
 * lasts without a heartbeat. After it, SpecTree stops showing the marker (the
 * app quit, slept or lost its connection).
 */
export const GROOMING_PREPARING_TTL_MS = 15 * 60_000;
/** Contract 3. How often the app sends a heartbeat for each batch it is preparing. */
export const GROOMING_HEARTBEAT_INTERVAL_MS = 2 * 60_000;
/** The `{ data: ... }` envelope every SpecTree success response uses. */
export function apiDataSchema(schema) {
    return z.object({ data: schema });
}
/** An ISO 8601 timestamp as JSON carries a Date. */
const isoDateTimeSchema = isoDateTime({ offset: true });
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/**
 * An ADO project name from the URL. A leading `_` is refused: `_` is the
 * placeholder the item routes accept, and no grooming read may run without a
 * real project.
 */
export const groomingProjectNameSchema = z
    .string()
    .trim()
    .min(1, "project must not be empty")
    .max(256)
    .refine((value) => !value.startsWith("_"), { message: "project must be a real ADO project name" });
/** `:project` path parameter. */
export const groomingProjectParamsSchema = z.object({ project: groomingProjectNameSchema }).strict();
/** An ADO work item id as a string: a positive integer of at most ten digits. */
export const groomingItemIdSchema = z
    .string()
    .trim()
    .regex(/^[1-9]\d{0,9}$/, "item ids must be positive integers");
/** A groom tag: one whole ADO tag (no `;`, which separates tags). */
export const groomTagSchema = z
    .string()
    .trim()
    .min(1, "tag must not be empty")
    .max(64)
    .regex(/^[^;]+$/, "tag must not contain ';'");
/** Batch item statuses, review states and batch statuses as Zod enums. */
export const groomingBatchItemStatusSchema = z.enum(GROOMING_BATCH_ITEM_STATUSES);
export const groomingReviewStateSchema = z.enum(GROOMING_REVIEW_STATES);
export const groomingBatchStatusSchema = z.enum(GROOMING_BATCH_STATUSES);
// ---------------------------------------------------------------------------
// Project config read
// ---------------------------------------------------------------------------
// ADO naming rules (learn.microsoft.com, "Naming restrictions"): the names the
// Repos page's pickers offer come from ADO, so every name ADO allows must
// save. A wider name stays safe downstream: the clone URL encodes each part
// (encodeURIComponent), and the Toolbox engine runs git through execFile (no
// shell) and turns each name into a path-safe folder name before using it.
/** Most characters in an ADO project or repository name. */
export const ADO_NAME_MAX = 64;
/** Printable characters ADO refuses in a repository name. */
const ADO_REPO_FORBIDDEN = /[\\/:*?"<>|;#${},+=[\]]/;
/** Printable characters ADO refuses in a project name (the repository set and `'`). */
const ADO_PROJECT_FORBIDDEN = /[\\/:*?"'<>|;#${},+=[\]]/;
/** Control characters, never allowed in an ADO name or a Git branch name. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
/**
 * True when `name` is a name ADO allows for a project (`kind: "project"`) or
 * a repository: 1-64 characters, no control character, none of ADO's
 * forbidden characters, no leading `_` or `.`, no trailing `.`, and no
 * leading or trailing space; and, stricter than ADO, no leading `-`. Spaces,
 * `&`, `(`, `)`, `!`, `@`, `%`, `~` and letters outside ASCII are allowed, as
 * in ADO. Pure.
 */
export function isAdoName(name, kind) {
    if (name.length === 0 || name.length > ADO_NAME_MAX)
        return false;
    if (name.trim() !== name)
        return false;
    if (CONTROL_CHARACTERS.test(name))
        return false;
    if ((kind === "project" ? ADO_PROJECT_FORBIDDEN : ADO_REPO_FORBIDDEN).test(name))
        return false;
    if (name.startsWith("_") || name.startsWith(".") || name.endsWith("."))
        return false;
    // Not an ADO rule: a leading "-" is refused so a name can never read as a command-line option.
    if (name.startsWith("-"))
        return false;
    return true;
}
/** Most characters in a branch name. */
export const GROOMING_BRANCH_MAX = 200;
/**
 * True when `name` is a Git branch name (`git check-ref-format` for
 * `refs/heads/<name>`) that is also safe as a git argument: no control
 * character, space or any of `~ ^ : ? * [ \`; no `..`, `@{` or `//`; not `@`;
 * no part starting with `.` or ending with `.lock`; not starting or ending
 * with `/`, not ending with `.`; and never starting with `-`. 1-200
 * characters. Pure.
 */
export function isGitBranchName(name) {
    if (name.length === 0 || name.length > GROOMING_BRANCH_MAX)
        return false;
    if (CONTROL_CHARACTERS.test(name) || /[ ~^:?*[\\]/.test(name))
        return false;
    if (name.startsWith("-") || name === "@")
        return false;
    if (name.includes("..") || name.includes("@{") || name.includes("//"))
        return false;
    if (name.startsWith("/") || name.endsWith("/") || name.endsWith("."))
        return false;
    return name.split("/").every((part) => !part.startsWith(".") && !part.endsWith(".lock"));
}
// ---------------------------------------------------------------------------
// GitHub-hosted repositories (epic PI-SPEC-SW-E318, ER-1683)
// ---------------------------------------------------------------------------
/** Most characters in a GitHub owner (organisation or user) name. */
export const GITHUB_OWNER_NAME_MAX = 39;
/** Most characters in a GitHub repository name. */
export const GITHUB_REPO_NAME_MAX = 100;
/**
 * The GitHub organisations SpecTree allows by default when an Admin maps a
 * GitHub repository. The list in force is server policy (the SpecTree API's
 * config); the contract carries only this default.
 */
export const GITHUB_ALLOWED_OWNERS_DEFAULT = ["TTC-SpecTree", "TTC-GGI", "TTC-POC"];
/**
 * True when `name` is a GitHub owner name: 1-39 ASCII letters, digits and
 * single hyphens, not starting or ending with a hyphen. Pure.
 */
export function isGitHubOwnerName(name) {
    if (name.length === 0 || name.length > GITHUB_OWNER_NAME_MAX)
        return false;
    return /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(name);
}
/**
 * True when `name` is a GitHub repository name: 1-100 of `A-Z a-z 0-9 . _ -`,
 * not `.` or `..`, not starting with `-` (it could read as a command-line
 * option) and not ending with `.git` (the clone URL adds that). Pure.
 */
export function isGitHubRepoName(name) {
    if (name.length === 0 || name.length > GITHUB_REPO_NAME_MAX)
        return false;
    if (!/^[A-Za-z0-9._-]+$/.test(name))
        return false;
    if (name === "." || name === "..")
        return false;
    if (name.startsWith("-"))
        return false;
    if (name.toLowerCase().endsWith(".git"))
        return false;
    return true;
}
/**
 * Reads owner, repository and (optionally) branch from a GitHub repository
 * URL an Admin pastes. Accepts only `https://github.com/<owner>/<repo>` (or
 * `www.github.com`, any letter case), with an optional trailing `/`, an
 * optional `.git` suffix (stripped) or an optional `/tree/<branch>`.
 * Refuses, with null: `http`, `ssh://`, `git@...`, a user name or password,
 * a port, a query, a fragment, any other host, extra path segments, and
 * names GitHub would refuse. Pure; makes no call to GitHub.
 */
export function parseGitHubRepoUrl(input) {
    if (typeof input !== "string")
        return null;
    const text = input.trim();
    if (text.length === 0 || text.length > 2048)
        return null;
    // Scheme and authority by hand: URL() would accept and normalise forms
    // (userinfo, default port, backslashes) this parser must refuse.
    const match = /^https:\/\/([^/?#]+)(\/[^?#]*)?$/i.exec(text);
    if (!match)
        return null;
    const authority = match[1] ?? "";
    const host = authority.toLowerCase();
    if (host !== "github.com" && host !== "www.github.com")
        return null;
    let path = match[2] ?? "";
    if (path.endsWith("/"))
        path = path.slice(0, -1);
    const segments = path.split("/").slice(1);
    if (segments.some((segment) => segment.length === 0))
        return null;
    const [owner, rawRepo, ...rest] = segments;
    if (owner === undefined || rawRepo === undefined)
        return null;
    if (!isGitHubOwnerName(owner))
        return null;
    let repo = rawRepo;
    let branch;
    if (rest.length === 0) {
        if (repo.toLowerCase().endsWith(".git"))
            repo = repo.slice(0, -".git".length);
    }
    else {
        if (rest[0] !== "tree" || rest.length < 2)
            return null;
        // GitHub percent-encodes branch characters in tree URLs (`%23` for `#`): decode once, refusing a malformed escape.
        try {
            branch = decodeURIComponent(rest.slice(1).join("/"));
        }
        catch {
            return null;
        }
        if (!isGitBranchName(branch))
            return null;
    }
    if (!isGitHubRepoName(repo))
        return null;
    return branch === undefined ? { owner, repo } : { owner, repo, branch };
}
/**
 * The fields of one mapped repository, without the per-host rules. Use
 * {@link groomingRepoSchema} to validate an entry; this object exists so a
 * caller can reuse one field (`groomingRepoObjectSchema.shape.project`).
 *
 * `project` keeps its ADO name rule here (it is only ever an ADO project);
 * `repo` and `owner` are checked per host by {@link groomingRepoSchema}.
 */
export const groomingRepoObjectSchema = z
    .object({
    /** ADO project that owns the repository, when it is not the configured project. ADO entries only. */
    project: z
        .string()
        .refine((value) => isAdoName(value, "project"), "project must be an Azure DevOps project name")
        .optional(),
    repo: z.string(),
    branch: z
        .string()
        .refine(isGitBranchName, "branch must be a Git branch name that does not start with '-'")
        .optional(),
    /** Where the code is hosted; absent = `"ado"` (epic PI-SPEC-SW-E318). */
    host: z.enum(GROOMING_REPO_HOSTS).optional(),
    /** GitHub organisation that owns the repository; GitHub entries only. */
    owner: z.string().optional(),
})
    .strict();
/**
 * One repository mapped to the project, as a person writes it in the config.
 *
 * - No `host`, or `host: "ado"`: `repo` (and `project`, when set) must be
 *   names ADO allows, and `owner` must be absent. Every entry stored before
 *   GitHub hosting validates unchanged.
 * - `host: "github"`: `owner` is required and must be a GitHub owner name,
 *   `repo` a GitHub repository name, and `project` must be absent (a GitHub
 *   repository hangs under the configured ADO project).
 *
 * Which GitHub owners may be mapped is server policy (the SpecTree API's
 * allowed list, default {@link GITHUB_ALLOWED_OWNERS_DEFAULT}), not checked here.
 */
export const groomingRepoSchema = groomingRepoObjectSchema.superRefine((entry, ctx) => {
    if (entry.host === "github") {
        if (entry.owner === undefined) {
            ctx.addIssue({ code: "custom", path: ["owner"], message: "A GitHub repository needs its owner (the GitHub organisation)." });
        }
        else if (!isGitHubOwnerName(entry.owner)) {
            ctx.addIssue({ code: "custom", path: ["owner"], message: "The owner must be a GitHub organisation name." });
        }
        if (!isGitHubRepoName(entry.repo)) {
            ctx.addIssue({ code: "custom", path: ["repo"], message: "repo must be a GitHub repository name, without the owner or .git." });
        }
        if (entry.project !== undefined) {
            ctx.addIssue({ code: "custom", path: ["project"], message: "A GitHub repository belongs to the configured project, so it cannot name another one." });
        }
        return;
    }
    if (entry.owner !== undefined) {
        ctx.addIssue({ code: "custom", path: ["owner"], message: "Only a GitHub repository has an owner." });
    }
    if (!isAdoName(entry.repo, "repository")) {
        ctx.addIssue({ code: "custom", path: ["repo"], message: "repo must be an Azure DevOps repository name" });
    }
});
/** One mapped repository in the config read, with the URL a runner clones it from. */
export const groomingRepoViewSchema = z.object({
    project: z.string().optional(),
    repo: z.string(),
    branch: z.string().optional(),
    /** Where the code is hosted; absent (an older SpecTree, or an ADO entry) = ADO. */
    host: z.enum(GROOMING_REPO_HOSTS).optional(),
    /** GitHub organisation, for a GitHub entry. */
    owner: z.string().optional(),
    /**
     * ADO: `{ADO_ORG_URL}/{project}/_git/{repo}`; GitHub:
     * `https://github.com/<owner>/<repo>.git`. Credentials are never part of it.
     */
    cloneUrl: z.string(),
});
/** `GET /projects/:project/config` → `data`. 404 GROOMING_CONFIG_NOT_FOUND when not set up. */
export const groomingConfigResponseSchema = z.object({
    tracker: z.string(),
    project: z.string(),
    enabled: z.boolean(),
    repos: z.array(groomingRepoViewSchema),
    /**
     * The GitHub organisations this SpecTree lets an Admin map (server policy,
     * epic PI-SPEC-SW-E318). Absent from an older SpecTree.
     */
    allowedGitHubOwners: z.array(z.string()).optional(),
    /** Null means the project's default ADO team. */
    teamName: z.string().nullable(),
    /** The tag the "tagged" mode uses (the stored tag, or the default). */
    groomTag: z.string(),
    groomTagIsDefault: z.boolean(),
    updatedById: z.string().nullable(),
    updatedAt: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Candidates
// ---------------------------------------------------------------------------
/** The selection modes that read candidates from the tracker. */
export const GROOMING_CANDIDATE_MODES = ["unestimated", "tagged"];
/** `GET /projects/:project/candidates?mode=unestimated|tagged&tag=`. */
export const groomingCandidatesQuerySchema = z
    .object({
    mode: z.enum(GROOMING_CANDIDATE_MODES),
    /** Only for `tagged`; defaults to the project's groom tag. */
    tag: groomTagSchema.optional(),
})
    .strict()
    .refine((query) => query.tag === undefined || query.mode === "tagged", {
    message: "tag is only allowed with mode=tagged",
    path: ["tag"],
});
/** One item picked for a batch. */
export const groomingCandidateSchema = z.object({
    itemId: z.string(),
    title: z.string(),
    url: z.string(),
    kind: z.string(),
    stage: z.enum(GROOMING_STAGES),
    teamPoints: z.number().nullable(),
});
/** One item left out because it is below the grooming bar. */
export const groomingNotReadyItemSchema = z.object({
    itemId: z.string(),
    title: z.string(),
    url: z.string(),
    /** Rubric labels of the rules the item does not meet. */
    missing: z.array(z.object({ id: z.string(), label: z.string(), severity: z.string() })),
});
/** `GET /projects/:project/candidates` → `data`. */
export const groomingCandidatesResponseSchema = z.object({
    project: z.string(),
    mode: z.enum(GROOMING_CANDIDATE_MODES),
    tag: z.string().nullable(),
    /** At most MAX_GROOMING_BATCH_ITEMS items, in backlog order. */
    selected: z.array(groomingCandidateSchema).max(MAX_GROOMING_BATCH_ITEMS),
    /** Eligible items beyond `selected`. */
    remaining: z.number().int().min(0),
    /** Items skipped because their latest brief is still current. */
    currentBriefCount: z.number().int().min(0),
    notReady: z.array(groomingNotReadyItemSchema),
});
// ---------------------------------------------------------------------------
// Batch lifecycle: requests
// ---------------------------------------------------------------------------
const batchItemIdsSchema = z
    .array(groomingItemIdSchema)
    .min(1, "name at least one item")
    .max(MAX_GROOMING_BATCH_ITEMS, `a batch holds at most ${String(MAX_GROOMING_BATCH_ITEMS)} items`);
/**
 * `POST /batches`. `single` needs exactly one item id (any user); `agenda`
 * may name items; `unestimated` and `tagged` are prefilled from the
 * candidates and take no item ids. Every mode but `single` needs a board
 * auditor or a global admin.
 */
export const createGroomingBatchBodySchema = z
    .object({
    project: groomingProjectNameSchema,
    mode: z.enum(GROOMING_BATCH_MODES),
    itemIds: batchItemIdsSchema.optional(),
    tag: groomTagSchema.optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.mode === "single" && body.itemIds?.length !== 1) {
        ctx.addIssue({ code: "custom", path: ["itemIds"], message: "single mode needs exactly one item id" });
    }
    if ((body.mode === "unestimated" || body.mode === "tagged") && body.itemIds !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["itemIds"],
            message: `${body.mode} batches are filled from the candidates; omit itemIds`,
        });
    }
    if (body.tag !== undefined && body.mode !== "tagged") {
        ctx.addIssue({ code: "custom", path: ["tag"], message: "tag is only allowed with mode=tagged" });
    }
});
/** `:id` path parameter of a batch. */
export const groomingBatchParamsSchema = z.object({ id: z.guid("id must be a UUID") }).strict();
/** `:id/items/:batchItemId` (PATCH): the batch item's own id. */
export const groomingBatchItemParamsSchema = z
    .object({ id: z.guid("id must be a UUID"), batchItemId: z.guid("batchItemId must be a UUID") })
    .strict();
/** `POST /batches/:id/items`. */
export const addGroomingBatchItemsBodySchema = z.object({ itemIds: batchItemIdsSchema }).strict();
// ---------------------------------------------------------------------------
// Sessions: move, add to a date, upcoming meetings (grooming contract 4,
// epic PI-SPEC-SW-E323, ER-1710). Agendas stay editable until the meeting
// date passes; a move or add names the meeting by its date.
// ---------------------------------------------------------------------------
/** A meeting date, `YYYY-MM-DD`, that exists on the calendar (no 2026-02-30). */
export const groomingSessionDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "must be a date as YYYY-MM-DD")
    .refine((value) => {
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, "must be a real calendar date");
/**
 * Move one agenda item to another upcoming meeting (`toDate`). The API
 * refuses a date that is not an upcoming meeting in the project's pattern
 * with `GROOMING_SESSION_DATE_NOT_UPCOMING` and a locked source agenda with
 * `GROOMING_AGENDA_LOCKED`.
 */
export const moveGroomingBatchItemBodySchema = z.object({ toDate: groomingSessionDateSchema }).strict();
/** `:project` and `:date` path parameters of one upcoming meeting. */
export const groomingSessionParamsSchema = z
    .object({ project: groomingProjectNameSchema, date: groomingSessionDateSchema })
    .strict();
/**
 * Add items to the agenda of the meeting named by the path's `:date`; the API
 * creates the draft agenda when that meeting has none. An item already on
 * another upcoming agenda stays there too (owner decision, 2026-11-04).
 */
export const addToSessionBodySchema = z.object({ itemIds: batchItemIdsSchema }).strict();
/** `POST /batches/:id/start` (engine): where and with which model the run happens. */
export const startGroomingBatchBodySchema = z
    .object({
    model: z.string().trim().min(1).max(64).optional(),
    runnerHost: z.string().trim().min(1).max(256).optional(),
})
    .strict()
    .default({});
/**
 * The item statuses the engine may set. `briefed` is set by the brief save.
 * `current` (contract 3, epic PI-SPEC-SW-E284) closes a queued item that
 * another run on the same computer just briefed: SpecTree accepts it only
 * when the item's latest brief still matches the live item.
 */
export const GROOMING_ENGINE_ITEM_STATUSES = ["running", "failed", "not-ready", "queued", "current"];
/** `PATCH /batches/:id/items/:batchItemId` (engine). */
export const patchGroomingBatchItemBodySchema = z
    .object({
    status: z.enum(GROOMING_ENGINE_ITEM_STATUSES),
    errorCode: z.string().trim().min(1).max(64).optional(),
    errorMessage: z.string().trim().min(1).max(1000).optional(),
})
    .strict();
/** `POST /batches/:id/finish` (engine). */
export const finishGroomingBatchBodySchema = z
    .object({
    status: z.enum(["completed", "stopped", "canceled"]),
    stopReason: z.enum(GROOMING_STOP_REASONS).optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.status === "stopped" && body.stopReason === undefined) {
        ctx.addIssue({ code: "custom", path: ["stopReason"], message: "a stopped batch needs a stopReason" });
    }
    if (body.status === "completed" && body.stopReason !== undefined) {
        ctx.addIssue({ code: "custom", path: ["stopReason"], message: "a completed batch has no stopReason" });
    }
});
// ---------------------------------------------------------------------------
// Batch lifecycle: responses
// ---------------------------------------------------------------------------
/** The latest brief of a batch item, summarised. */
export const groomingBatchItemBriefSchema = z.object({
    id: z.string(),
    suggestedPoints: z.number(),
    pointsLow: z.number(),
    pointsHigh: z.number(),
    confidence: z.string(),
    commentStatus: z.string(),
    createdAt: isoDateTimeSchema,
});
/** One item of a batch. `PATCH /batches/:id/items/:batchItemId` → `data`. */
export const groomingBatchItemSchema = z.object({
    id: z.string(),
    batchId: z.string(),
    itemId: z.string(),
    title: z.string(),
    position: z.number().int(),
    status: groomingBatchItemStatusSchema,
    reviewState: groomingReviewStateSchema,
    briefId: z.string().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    startedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
});
/** One item of a batch with its latest brief, as the batch reads return it. */
export const groomingBatchItemViewSchema = groomingBatchItemSchema.extend({
    brief: groomingBatchItemBriefSchema.nullable(),
});
/**
 * A batch with its items. `GET /batches/:id`, `POST /batches/:id/items` and
 * `POST /batches/:id/finish` → `data`.
 */
export const groomingBatchSchema = z.object({
    id: z.string(),
    tracker: z.string(),
    /** The tracker project (the ADO project name). */
    container: z.string(),
    mode: z.enum(GROOMING_BATCH_MODES),
    status: groomingBatchStatusSchema,
    tag: z.string().nullable(),
    stopReason: z.enum(GROOMING_STOP_REASONS).nullable(),
    model: z.string().nullable(),
    runnerHost: z.string().nullable(),
    createdById: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    startedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    items: z.array(groomingBatchItemViewSchema).max(MAX_GROOMING_BATCH_ITEMS),
});
/** What creating a batch from the candidates left out. */
export const groomingBatchPrefillSchema = z.object({
    remaining: z.number().int().min(0),
    currentBriefCount: z.number().int().min(0),
    notReady: z.array(groomingNotReadyItemSchema),
});
/** `POST /batches` → `data` (201). `prefill` is set for `unestimated` and `tagged`. */
export const createGroomingBatchResponseSchema = z.object({
    batch: groomingBatchSchema,
    prefill: groomingBatchPrefillSchema.nullable(),
});
/** One item the engine should brief, in position order. */
export const groomingRunnableItemSchema = z.object({ batchItemId: z.string(), itemId: z.string() });
/** `POST /batches/:id/start` → `data`: the started batch and the items still to brief. */
export const startGroomingBatchResponseSchema = z.object({
    batch: groomingBatchSchema,
    runnable: z.array(groomingRunnableItemSchema).max(MAX_GROOMING_BATCH_ITEMS),
});
/** Most upcoming meetings the upcoming-sessions read lists. */
export const GROOMING_UPCOMING_SESSIONS_MAX = 12;
/**
 * One upcoming meeting (grooming contract 4). `batchId` and `status` are null
 * when the meeting has no agenda yet; `isNext` marks the next meeting, which
 * the Session page opens on.
 */
export const groomingUpcomingSessionSchema = z.object({
    date: groomingSessionDateSchema,
    batchId: z.string().nullable(),
    status: groomingBatchStatusSchema.nullable(),
    itemCount: z.number().int().min(0),
    isNext: z.boolean(),
});
/**
 * The upcoming meetings of a project, earliest first, with the pattern's time
 * zone (null when the project has no meeting pattern).
 */
export const groomingUpcomingSessionsResponseSchema = z.object({
    timeZone: z.string().nullable(),
    sessions: z.array(groomingUpcomingSessionSchema).max(GROOMING_UPCOMING_SESSIONS_MAX),
});
/**
 * A move's result (grooming contract 4). `source` is the agenda the item left,
 * null when there is no source agenda to return; `target` is the agenda it is
 * on now. `alreadyOnTarget` is true when the item was on the target already,
 * `createdTarget` when the move created the target meeting's draft agenda.
 */
export const moveGroomingBatchItemResponseSchema = z.object({
    source: groomingBatchSchema.nullable(),
    target: groomingBatchSchema,
    alreadyOnTarget: z.boolean(),
    createdTarget: z.boolean(),
});
// ---------------------------------------------------------------------------
// Disregard a brief (contract 5, ER-1712)
// ---------------------------------------------------------------------------
/**
 * Grooming contract 5. The comment statuses an unposted brief may have before
 * it is disregarded, and the only ones an undo may restore. A `posted` brief
 * is on ADO and cannot be disregarded (`GROOMING_BRIEF_POSTED`).
 */
export const GROOMING_DISREGARD_PREVIOUS_COMMENT_STATUSES = ["held", "skipped", "failed"];
/** Contract 5. The batch item statuses a disregard may move back to "needs brief", and an undo restore. */
export const GROOMING_DISREGARD_PREVIOUS_ITEM_STATUSES = ["briefed", "current"];
/** Contract 5. The review states a disregarded item may have had, and an undo restore. */
export const GROOMING_DISREGARD_PREVIOUS_REVIEW_STATES = ["pending", "skipped"];
/** Contract 5. How long after a disregard its undo token is accepted, in milliseconds. */
export const GROOMING_DISREGARD_UNDO_TTL_MS = 15 * 60 * 1000;
/**
 * Contract 5. What a disregard changed, so the same person can undo it. The
 * token travels through the client (no column stores it) and is opaque to it:
 * SpecTree signs it (`signature`, bound to the brief and every value here),
 * accepts it for {@link GROOMING_DISREGARD_UNDO_TTL_MS} after `issuedAt`, and
 * refuses a changed, expired or replayed token, a newer brief, or an item that
 * moved on with `GROOMING_DISREGARD_UNDO_UNAVAILABLE`. A brief is linked to
 * very few items; the cap only bounds the body.
 */
export const groomingDisregardUndoSchema = z
    .object({
    previousCommentStatus: z.enum(GROOMING_DISREGARD_PREVIOUS_COMMENT_STATUSES),
    items: z
        .array(z
        .object({
        batchItemId: z.string().min(1).max(64),
        previousStatus: z.enum(GROOMING_DISREGARD_PREVIOUS_ITEM_STATUSES),
        previousReviewState: z.enum(GROOMING_DISREGARD_PREVIOUS_REVIEW_STATES),
        /** The item's preparation start and finish before the disregard; Undo puts them back. */
        previousStartedAt: z.iso.datetime().nullable(),
        previousFinishedAt: z.iso.datetime().nullable(),
    })
        .strict())
        .max(MAX_GROOMING_BATCH_ITEMS * 5),
    /** When SpecTree issued the token (ISO 8601). */
    issuedAt: z.iso.datetime(),
    /** SpecTree's signature over the brief id and every other value; never computed by a client. */
    signature: z.string().regex(/^[0-9a-f]{64}$/),
})
    .strict();
/**
 * `POST .../grooming-briefs/:briefId/disregard` → `data` (contract 5). The
 * brief is now `discarded`; `batchItemIds` are the batch items that went back
 * to "needs brief". `undo` is the body for the undo route.
 */
export const disregardGroomingBriefResponseSchema = z.object({
    briefId: z.string(),
    commentStatus: z.literal("discarded"),
    batchItemIds: z.array(z.string()),
    undo: groomingDisregardUndoSchema,
});
/** `POST .../grooming-briefs/:briefId/undo-disregard` body (contract 5): the `undo` a disregard returned. */
export const undoDisregardGroomingBriefBodySchema = groomingDisregardUndoSchema;
/**
 * `POST .../grooming-briefs/:briefId/undo-disregard` → `data` (contract 5).
 * The brief's restored comment status and the batch items restored with it.
 */
export const undoDisregardGroomingBriefResponseSchema = z.object({
    briefId: z.string(),
    commentStatus: z.enum(GROOMING_DISREGARD_PREVIOUS_COMMENT_STATUSES),
    batchItemIds: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Backlog "preparing" marker (epic PI-SPEC-SW-E283; state added in contract 3)
// ---------------------------------------------------------------------------
/**
 * Where an item stands in the run preparing it: waiting in the queue
 * (`queued`) or being briefed now (`running`).
 */
export const GROOMING_PREPARING_STATES = ["queued", "running"];
/**
 * Someone is preparing this backlog item in a running batch: the backlog row's
 * `preparing` field (null when nobody is). Informational only: it never hides
 * the row or disables an action. `byUserId` is null when the batch owner's
 * account was removed; `startedAt` is when the batch started (ISO 8601).
 * `state` (contract 3) says whether the item is still queued or running.
 */
export const groomingPreparingSchema = z.object({
    byUserId: z.string().nullable(),
    byName: z.string().nullable(),
    startedAt: z.string().nullable(),
    state: z.enum(GROOMING_PREPARING_STATES),
});
// ---------------------------------------------------------------------------
// Author-question drafts (contract 2, epic PI-SPEC-SW-E280)
// ---------------------------------------------------------------------------
/** Most questions one draft for an item's author may hold (same cap as posting them). */
export const MAX_AUTHOR_QUESTIONS = 10;
/** Longest single question for an item's author (same cap as posting them). */
export const AUTHOR_QUESTION_MAX_CHARS = 500;
/**
 * `PUT /api/v1/work-items/:tracker/:project/:itemId/author-questions/draft`:
 * the questions SpecTree Toolbox drafted with Claude Code. Same caps as the
 * body that posts them to the tracker (1-10 questions, each 1-500 characters
 * after trimming).
 */
export const saveAuthorQuestionsDraftBodySchema = z
    .object({
    questions: z
        .array(z.string().trim().min(1).max(AUTHOR_QUESTION_MAX_CHARS))
        .min(1)
        .max(MAX_AUTHOR_QUESTIONS),
})
    .strict();
/** The stored draft. `PUT` and `GET .../author-questions/draft` → `data`. */
export const authorQuestionsDraftResponseSchema = z.object({
    id: z.string(),
    itemId: z.string(),
    questions: z.array(z.string()),
    /** True while this draft is current for the item; SpecTree decides when it goes stale. */
    isCurrent: z.boolean(),
    createdAt: isoDateTimeSchema,
    createdById: z.string().nullable(),
});
//# sourceMappingURL=api.js.map